import { chartingQueryKeys } from '@/features/charting-next/api/chartingCache';
import { fetchChartingFinderDefinition } from '@/features/charting-next/api/chartingFinderRepository';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { computed, ref } from '@vue/composition-api';

export function useChartingFinderDefinition({ sessionStore, capabilities }) {
    const finderRevision = ref(0);
    const defaultFinderDefinitionResource = useRemoteResource({
        key: () => chartingQueryKeys.finderDefinition(sessionStore.uid),
        loader: () => fetchChartingFinderDefinition(),
        ttl: 120000,
        enabled: () => sessionStore.isAuthenticated && capabilities.value.canUseFilters
    });
    const resolveActiveResource = () => defaultFinderDefinitionResource;
    const finderDefinitionResource = {
        data: computed(() => resolveActiveResource().data.value),
        error: computed(() => resolveActiveResource().error.value),
        status: computed(() => resolveActiveResource().status.value),
        lastLoadedAt: computed(() => resolveActiveResource().lastLoadedAt.value),
        isLoading: computed(() => resolveActiveResource().isLoading.value),
        isRefreshing: computed(() => resolveActiveResource().isRefreshing.value),
        isReady: computed(() => resolveActiveResource().isReady.value),
        isEmpty: computed(() => resolveActiveResource().isEmpty.value),
        load: (options) => resolveActiveResource().load(options),
        refresh: () => resolveActiveResource().refresh()
    };

    const activeFinderBase = computed(() => finderDefinitionResource.data.value?.defaultFinder || {});
    const mutableFinderBase = computed(() => defaultFinderDefinitionResource.data.value?.defaultFinder || {});
    const canRefreshFinderDefinition = () => sessionStore.isAuthenticated && capabilities.value.canUseFilters;

    const bumpFinderRevision = () => {
        finderRevision.value += 1;
    };

    const refreshActiveFinderDefinition = async () => {
        if (!canRefreshFinderDefinition()) {
            return null;
        }

        return resolveActiveResource().refresh();
    };

    const refreshMutableFinderDefinition = async ({ bumpRevision = false } = {}) => {
        try {
            if (!canRefreshFinderDefinition()) {
                return null;
            }

            return await defaultFinderDefinitionResource.refresh();
        } finally {
            if (bumpRevision) {
                bumpFinderRevision();
            }
        }
    };

    return {
        finderDefinitionResource,
        defaultFinderDefinitionResource,
        finderRevision,
        activeFinderBase,
        mutableFinderBase,
        refreshActiveFinderDefinition,
        refreshMutableFinderDefinition
    };
}
